import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/useStore';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { NoShowRiskBadge, NoShowRiskStats } from '../components/reservations/NoShowRiskBadge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  Users,
  Clock,
  RefreshCw,
  Send,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RiskDashboard() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [highRiskReservations, setHighRiskReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [riskRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/predictions/high-risk`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/predictions/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (riskRes.ok) {
        const riskData = await riskRes.json();
        setHighRiskReservations(riskData.reservations || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const sendReminder = async (reservation) => {
    try {
      // This would trigger a reminder via SMS/Email
      toast.success(`Recordatorio enviado a ${reservation.guest_name}`);
    } catch (err) {
      toast.error('Error al enviar recordatorio');
    }
  };

  const requestDeposit = async (reservation) => {
    try {
      // This would send a deposit request
      toast.success(`Solicitud de depósito enviada a ${reservation.guest_name}`);
    } catch (err) {
      toast.error('Error al solicitar depósito');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <TopBar title="Predicción de No-Shows" helpSection="risk-dashboard" />
        <div className="p-6 animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="risk-dashboard">
      <TopBar title="Predicción de No-Shows" helpSection="risk-dashboard">
        <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Actualizar
        </Button>
      </TopBar>

      <div className="p-6 space-y-6">
        {/* Stats */}
        {stats && <NoShowRiskStats stats={stats} />}

        {/* High Risk Reservations */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <CardTitle>Reservaciones de Alto Riesgo</CardTitle>
              </div>
              <Badge variant="outline">{highRiskReservations.length} reservas</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {highRiskReservations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No hay reservaciones de alto riesgo para hoy o mañana</p>
              </div>
            ) : (
              <div className="space-y-3">
                {highRiskReservations.map((res) => (
                  <div
                    key={res.id}
                    className={cn(
                      "p-4 rounded-xl border",
                      res.no_show_prediction?.risk_level === 'high' 
                        ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30"
                        : "border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30"
                    )}
                    data-testid={`risk-reservation-${res.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium">{res.guest_name}</h4>
                          <NoShowRiskBadge 
                            prediction={res.no_show_prediction} 
                            showDetails={true}
                            size="default"
                          />
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {res.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {res.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {res.party_size} personas
                          </span>
                        </div>

                        {res.guest_phone && (
                          <p className="text-sm flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            {res.guest_phone}
                          </p>
                        )}

                        {/* Factors */}
                        {res.no_show_prediction?.factors?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {res.no_show_prediction.factors.map((factor, i) => (
                              <Badge 
                                key={i} 
                                variant="outline"
                                className={cn(
                                  "text-xs",
                                  factor.impact === 'high' ? "border-red-300 text-red-700" :
                                  factor.impact === 'medium' ? "border-yellow-300 text-yellow-700" :
                                  "border-blue-300 text-blue-700"
                                )}
                              >
                                {factor.factor}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => sendReminder(res)}
                          data-testid={`send-reminder-${res.id}`}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Recordatorio
                        </Button>
                        
                        {res.no_show_prediction?.should_require_deposit && !res.deposit_paid && (
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => requestDeposit(res)}
                            data-testid={`request-deposit-${res.id}`}
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Pedir Depósito
                          </Button>
                        )}

                        {res.guest_phone && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`tel:${res.guest_phone}`)}
                          >
                            <Phone className="h-3 w-3 mr-1" />
                            Llamar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle>Recomendaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-xl">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-green-500" />
                  Recordatorios Automáticos
                </h4>
                <p className="text-sm text-muted-foreground">
                  Envía recordatorios SMS 24h antes a todas las reservas de alto riesgo.
                </p>
              </div>
              <div className="p-4 bg-muted rounded-xl">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-blue-500" />
                  Depósitos
                </h4>
                <p className="text-sm text-muted-foreground">
                  Solicita depósito para clientes con historial de no-shows o grupos grandes.
                </p>
              </div>
              <div className="p-4 bg-muted rounded-xl">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-orange-500" />
                  Confirmación Telefónica
                </h4>
                <p className="text-sm text-muted-foreground">
                  Para reservas de muy alto riesgo, confirma por teléfono el día anterior.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
